import { Injectable } from '@angular/core';

/**
 * Service to handle image loading fallbacks and maintain a record of failed URLs
 */
@Injectable({
  providedIn: 'root'
})
export class FallbackImageService {
  // Cache for known broken image URLs
  private failedUrls = new Set<string>();
  
  // Note: No default profile image needed - we always generate initials
  
  /**
   * Get a safe image URL, returning a fallback if the URL is known to fail
   * FIN-0104: Added isBot parameter for distinct avatar colors
   * @param url The original image URL
   * @param fullName Optional full name for generating initials (e.g., "John Doe")
   * @param isBot Whether this is for a bot/assistant avatar
   * @returns Safe URL to use (original or fallback)
   */
  getSafeImageUrl(url: string, fullName?: string, isBot: boolean = false): string {
    // For empty, null, or obviously invalid URLs, return initials or default
    if (!url || url.trim() === '' || url === 'null' || url === 'undefined') {
      return this.generateInitialsAvatar(fullName, isBot);
    }

    // Return initials if this URL previously failed
    if (this.failedUrls.has(url)) {
      return this.generateInitialsAvatar(fullName, isBot);
    }

    // For local assets, assume they're available
    if (url.startsWith('assets/')) {
      return url;
    }

    // For data URLs (our generated avatars), return as-is
    if (url.startsWith('data:')) {
      return url;
    }

    // For suspicious URLs that look invalid, use initials
    if (url.length < 4 || (!url.startsWith('http') && !url.startsWith('/') && !url.startsWith('assets/'))) {
      return this.generateInitialsAvatar(fullName, isBot);
    }

    // Otherwise return the original URL
    return url;
  }
  
  /**
   * Mark a URL as failed so it won't be used again
   * @param url The URL that failed to load
   */
  markUrlAsFailed(url: string): void {
    if (url && !url.startsWith('assets/')) {
      this.failedUrls.add(url);
    }
  }
  
  /**
   * Get a fallback when no name is available (generates "?" initial)
   * @returns Data URL for generic avatar
   */
  getDefaultProfileImage(): string {
    return this.generateInitialsAvatar('Unknown');
  }

  /**
   * Generate an initials-based avatar as a data URL
   * FIN-0104: Distinct colors for bot vs user - separating identity from brand
   * @param fullName The user's full name (e.g., "John Doe")
   * @param isBot Whether this is for the bot/assistant avatar
   * @returns Data URL for the generated avatar or default image
   */
  generateInitialsAvatar(fullName?: string, isBot: boolean = false): string {
    // For bot, use "F" for Finderwise; for users, extract initials
    const initials = isBot ? 'F' : (fullName ? this.extractInitials(fullName) : 'U');
    const displayInitials = initials || 'U';

    // FIN-0104: Use distinct colors for bot vs user (not brand color)
    // Bot: soft mint background, dark sage text - calming, distinct from buttons
    // User: warm gray background, stone text - neutral, doesn't compete
    const backgroundColor = isBot ? '#e8f5ed' : '#e7e5e4';  // assistant-bg / user-bg
    const textColor = isBot ? '#2c4a3c' : '#57534e';        // assistant-text / user-text

    // FIN-0104: Create SVG avatar with initials - rounded rectangle (12px) to match unified shape language
    const svg = `<svg width="32" height="32" viewBox="0 0 32 32" xmlns="http://www.w3.org/2000/svg">
      <rect width="32" height="32" rx="12" fill="${backgroundColor}"/>
      <text x="16" y="21" text-anchor="middle" fill="${textColor}" font-family="Roboto, sans-serif" font-size="14" font-weight="600">${displayInitials}</text>
    </svg>`;

    // Convert to data URL using URL encoding instead of base64 (better Angular support)
    const encodedSvg = encodeURIComponent(svg);
    const dataUrl = 'data:image/svg+xml,' + encodedSvg;
    return dataUrl;
  }

  /**
   * Extract initials from a full name
   * @param fullName The user's full name
   * @returns Initials (up to 2 characters) or empty string
   */
  private extractInitials(fullName: string): string {
    const trimmed = fullName.trim();
    if (!trimmed) return '';

    const words = trimmed.split(/\s+/);
    if (words.length === 1) {
      // Single word - take first character
      return words[0].charAt(0).toUpperCase();
    } else {
      // Multiple words - take first character of first and last word
      const first = words[0].charAt(0).toUpperCase();
      const last = words[words.length - 1].charAt(0).toUpperCase();
      return first + last;
    }
  }

  /**
   * Generate avatar background color
   * FIN-0103: Single brand color for visual consistency
   * @param name The user's name (unused, kept for API compatibility)
   * @returns The brand sage green color
   */
  private generateColorFromName(name: string): string {
    // FIN-0103: Use single brand sage green for all avatars
    // This creates visual consistency throughout the app
    return '#5a9e7a'; // Brand sage green (matches Tailwind brand.DEFAULT)
  }
  
  /**
   * Clear the cache of failed URLs
   */
  clearFailedUrls(): void {
    this.failedUrls.clear();
  }
}